<template>
	<div class="documents-view">
		<header class="documents-header">
			<h1 id="documents-heading" class="documents-title">{{ documentsHeading }}</h1>
		</header>

		<section
			id="documents-panel"
			class="documents-panel"
			:class="`documents-panel--${activeAudience}`"
			role="region"
			aria-labelledby="documents-heading"
		>
			<KeepAlive>
				<component
					:is="activePanelComponent"
					:key="activeAudience"
					@open-document="handleOpenDocument"
					@convert-to-invoice="handleConvertToInvoice"
					@open-invoice="handleOpenInvoice"
					@create-template="handleCreateTemplate"
					@document-transferred="handleDocumentTransferred"
					@open-record="(payload) => emit('open-record', payload)"
					@record-deleted="(payload) => emit('record-deleted', payload)"
				/>
			</KeepAlive>
		</section>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import PortalDocuments from '@/components/settings/PortalDocuments.vue';
import ClientDocumentsListView from '@/views/client-documents/ClientDocumentsListView.vue';
import CompanyRecordsListView from '@/views/company-records/CompanyRecordsListView.vue';

const AUDIENCE_VALUES = new Set(['clients', 'suppliers', 'records']);
const PANELS = {
	clients: { component: ClientDocumentsListView, heading: 'Client documents' },
	suppliers: { component: PortalDocuments, heading: 'Supplier documents' },
	records: { component: CompanyRecordsListView, heading: 'Company records' },
};

const props = defineProps({
	audience: {
		type: String,
		default: 'clients',
	},
});

const emit = defineEmits([
	'open-document',
	'convert-to-invoice',
	'open-invoice',
	'create-template',
	'document-transferred',
	'open-record',
	'record-deleted',
]);

const normalizeAudience = (value) =>
	AUDIENCE_VALUES.has(String(value || '').toLowerCase())
		? String(value).toLowerCase()
		: 'clients';

const activeAudience = ref(normalizeAudience(props.audience));

const activePanelComponent = computed(() => PANELS[activeAudience.value].component);

const documentsHeading = computed(() => PANELS[activeAudience.value].heading);

watch(
	() => props.audience,
	(value) => {
		const nextAudience = normalizeAudience(value);
		if (nextAudience !== activeAudience.value) {
			activeAudience.value = nextAudience;
		}
	},
);

const handleOpenDocument = (payload = {}) => {
	emit('open-document', {
		...(payload || {}),
		domain: activeAudience.value === 'suppliers' ? 'supplier' : 'client',
	});
};

const handleCreateTemplate = (payload = {}) => {
	if (activeAudience.value !== 'suppliers') return;
	emit('create-template', {
		...(payload || {}),
		domain: 'supplier',
	});
};

const handleConvertToInvoice = (payload = {}) => {
	if (activeAudience.value !== 'clients') return;
	emit('convert-to-invoice', {
		...(payload || {}),
		sourceDomain: 'client',
	});
};

const handleOpenInvoice = (payload = {}) => {
	if (activeAudience.value !== 'clients') return;
	emit('open-invoice', {
		...(payload || {}),
		sourceDomain: 'client',
	});
};

const handleDocumentTransferred = (payload = {}) => {
	if (activeAudience.value !== 'clients') return;
	emit('document-transferred', {
		...(payload || {}),
		sourceDomain: 'client',
		destinationDomain: 'supplier',
	});
};
</script>

<style scoped>
.documents-view {
	display: flex;
	flex-direction: column;
	min-width: 0;
	min-height: 100%;
	gap: 16px;
	box-sizing: border-box;
	padding: 24px;
	color: var(--text-color);
}

.documents-header {
	display: flex;
	align-items: center;
	min-height: 40px;
}

.documents-title {
	margin: 0;
	font: 600 1.5rem/2rem var(--font-family-display);
	color: var(--text-color);
}

.documents-panel {
	min-width: 0;
	min-height: 0;
}

.documents-panel--clients :deep(.client-documents-list) {
	padding: 0;
}

.documents-panel--clients :deep(.list-header),
.documents-panel--suppliers :deep(.header-bar) {
	display: none;
}

@media (max-width: 640px) {
	.documents-view {
		padding: 16px;
	}

}
</style>
